import React, { Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import usePrevious from '@dashboard/hooks/usePrevious';
import Button from './Button';

const Dialog = ({
  title,
  children,
  size = 'lg',
  description,
  trigger,
  cancelButton = {
    label: 'Cancel',
    appearance: 'link',
  },
  confirmButton = {
    label: 'Confirm',
    appearance: 'primary',
    disabled: false,
    onClick: () => {},
  },
  onCancel,
  defaultIsOpen = false,
  open = false,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(defaultIsOpen);
  const previousDialogOpen = usePrevious(dialogOpen);

  React.useEffect(() => {
    if (open) {
      setDialogOpen(true);
    } else {
      setDialogOpen(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!onCancel || typeof onCancel !== 'function') {
      return;
    }

    if (previousDialogOpen && !dialogOpen) {
      onCancel();
    }
  }, [previousDialogOpen, dialogOpen, onCancel]);

  return (
    <DialogPrimitive.Root
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      defaultOpen={defaultIsOpen}
    >
      {trigger && <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>}

      <DialogPrimitive.Portal>
        <Transition show={dialogOpen}>
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <Transition
              as="div"
              className={clsx(
                'fixed inset-0 bg-black/50',
                'transition duration-300 ease-in',
                'data-[closed]:opacity-0',
              )}
            >
              <DialogPrimitive.Overlay />
            </Transition>

            <div className="min-h-full flex items-center justify-center">
              <Transition>
                <DialogPrimitive.Content
                  className={clsx(
                    'relative z-50 mx-auto',
                    'w-[95vw] max-h-[90vh] rounded-lg p-6',
                    'bg-white overflow-auto',
                    'focus:outline-none focus-visible:ring-3 focus-visible:ring-chocolate-500',
                    'transition ease-in-out duration-300',
                    'data-[closed]:opacity-0 data-[closed]:scale-95',
                    'data-[enter]:duration-200',
                    'data-[leave]:duration-150',
                    {
                      'max-w-md': size === 'md',
                      'max-w-lg': size === 'lg',
                      'max-w-xl': size === 'xl',
                      'max-w-2xl': size === '2xl',
                      'max-w-3xl': size === '3xl',
                      'max-w-5xl': size === '5xl',
                    },
                  )}
                >
                  {title && (
                    <DialogPrimitive.Title className="text-2xl font-bold text-gray-900">
                      {title}
                    </DialogPrimitive.Title>
                  )}

                  {description && (
                    <DialogPrimitive.Description className="mt-2 text-base text-gray-700 ">
                      {description}
                    </DialogPrimitive.Description>
                  )}

                  <div className="mt-4">
                    {typeof children === 'function'
                      ? children({
                          close: () => setDialogOpen(false),
                        })
                      : children}
                  </div>

                  {(cancelButton || confirmButton) && (
                    <div className="mt-6 flex justify-end space-x-4">
                      {cancelButton && (
                        <DialogPrimitive.Close onClick={onCancel} asChild>
                          <Button {...cancelButton}>{cancelButton.label}</Button>
                        </DialogPrimitive.Close>
                      )}

                      {confirmButton && <Button {...confirmButton}>{confirmButton.label}</Button>}
                    </div>
                  )}

                  <DialogPrimitive.Close asChild>
                    <button className="absolute top-4 right-4 text-gray-900 hover:text-chocolate-600 transition-colors focus:outline-none">
                      <XMarkIcon width="24px" height="24px" className="fill-current" />
                    </button>
                  </DialogPrimitive.Close>
                </DialogPrimitive.Content>
              </Transition>
            </div>
          </div>
        </Transition>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default Dialog;
